/* eslint-disable @typescript-eslint/no-empty-function */
import { Component } from '@angular/core';
import { AppColors, MonthlyTransactionDto, TimeIntervalPeriod } from '@frontend/common';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration, ChartData } from 'chart.js';

@Component({
	selector: 'frontend-transaction-chart',
	templateUrl: './transaction-chart.component.html',
	styleUrls: ['./transaction-chart.component.scss'],
	standalone: false,
})
export class TransactionChartComponent {
	public chartData: ChartData<'line'> = { labels: [], datasets: [] };
	public chartOptions: ChartConfiguration<'line'>['options'];
	public tooltipValues: number[] = [];

	public lineChartOptions: ChartConfiguration<'line'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		layout: {
			padding: {
				top: 40,
				bottom: 16,
				left: 32,
				right: 32,
			},
		},
		plugins: {
			legend: {
				display: true,
				position: 'bottom',
				align: 'start',
				labels: {
					boxWidth: 16,
					boxHeight: 16,
					usePointStyle: true,
					pointStyle: 'rectRounded',
					font: {
						family: 'Open Sans',
						size: 14,
					},
					color: AppColors.DarkBlue,
				},
				onClick: () => {},
			},
			tooltip: {
				padding: {
					top: 8,
					bottom: 4,
					left: 8,
					right: 8,
				},
				titleFont: { size: 0 },
				bodyFont: { size: 14 },
				backgroundColor: AppColors.White,
				titleColor: AppColors.DarkBlue,
				bodyColor: AppColors.DarkBlue,
				borderColor: AppColors.LightGrey,
				borderWidth: 1,
				cornerRadius: 10,
				displayColors: false,
				position: 'nearest',
				yAlign: 'bottom',
				callbacks: {
					title: () => '',
					label: (context) => {
						const index = context.dataIndex;
						const realValue = this.tooltipValues[index];
						return `€${Number(realValue).toLocaleString('de-DE')}`;
					},
				},
			},
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: { color: AppColors.DarkBlue },
			},
			y: {
				grid: { display: false },
				ticks: {
					color: AppColors.DarkBlue,
					stepSize: 10_000,
					callback: (value) => `€${Number(value).toLocaleString('de-DE')}`,
				},
				suggestedMin: 0,
				suggestedMax: 50_000,
			},
		},
	};

	public noDataPlugin = {
		id: 'noDataMessage',
		beforeDraw: (chart: Chart) => {
			const datasets = chart.data.datasets;
			const allZeroOrNull = datasets.every(
				(ds) => Array.isArray(ds.data) && ds.data.every((val) => val === null || val === 0),
			);

			if (!allZeroOrNull) return;

			const { ctx, chartArea } = chart;
			const message = this.translateService.instant('dashboard.transactions.noData');

			ctx.save();
			ctx.font = '16px Open Sans';
			ctx.fillStyle = AppColors.Midnight;
			ctx.textAlign = 'center';
			ctx.fillText(message, (chartArea.left + chartArea.right) / 2, (chartArea.top + chartArea.bottom) / 2);
			ctx.restore();
		},
	};

	constructor(public translateService: TranslateService) {}

	public loadChartData(data: MonthlyTransactionDto[], timePeriod: TimeIntervalPeriod): void {
		this.tooltipValues = data.map((entry) => entry.totalAmount);
		const labels = this.generateLabels(data);
		const values = this.mapValuesWithGaps(data, timePeriod);
		this.setLineChartData(labels, values);
	}

	private mapValuesWithGaps(data: MonthlyTransactionDto[], period: TimeIntervalPeriod): (number | null)[] {
		const MAX_VISUAL_VALUE = 50000;
		const currentMonthIndex = new Date().getMonth();
		if (period === TimeIntervalPeriod.YEARLY) {
			return data.map((entry) => {
				const monthIndex = entry.month - 1;
				const isCurrentOrPast = monthIndex <= currentMonthIndex;

				return isCurrentOrPast ? Math.min(entry.totalAmount, MAX_VISUAL_VALUE) : null;
			});
		}

		if (period === TimeIntervalPeriod.MONTHLY) {
			const onlyValue = data[0]?.totalAmount ?? 0;
			return [onlyValue > 0 ? Math.min(onlyValue, MAX_VISUAL_VALUE) : null];
		}

		return data.map((entry) => Math.min(entry.totalAmount, MAX_VISUAL_VALUE));
	}

	private generateLabels(data: MonthlyTransactionDto[]): string[] {
		const monthKeys = [
			'general.monthPrefix.january',
			'general.monthPrefix.february',
			'general.monthPrefix.march',
			'general.monthPrefix.april',
			'general.monthPrefix.may',
			'general.monthPrefix.june',
			'general.monthPrefix.july',
			'general.monthPrefix.august',
			'general.monthPrefix.september',
			'general.monthPrefix.october',
			'general.monthPrefix.november',
			'general.monthPrefix.december',
		];

		return data.map((entry) => {
			const key = monthKeys[entry.month - 1];
			return this.translateService.instant(key);
		});
	}

	private setLineChartData(labels: string[], values: (number | null)[]): void {
		const allZeroOrNull = values.every((val) => val === null || val === 0);

		this.chartData = {
			labels,
			datasets: [
				{
					data: values,
					label: this.translateService.instant('dashboard.transactions.valueOfTransactions'),
					fill: false,
					borderColor: AppColors.Green,
					backgroundColor: AppColors.Green,
					pointBackgroundColor: AppColors.Green,
					pointBorderColor: AppColors.Green,
					tension: 0,
					showLine: !allZeroOrNull,
					pointRadius: allZeroOrNull ? 0 : 3,
					pointHoverRadius: allZeroOrNull ? 0 : 4,
				},
			],
		};
	}
}
